import "../index.css"
import { ThemeProvider } from "@/components/theme-provider"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Construction } from "lucide-react"
import { QRCodeSVG } from "qrcode.react"
import { BrowserRouter, Routes, Route } from "react-router-dom"
import { AppDownloadPage } from "@/pages"
import { useAutoTranslation } from "@/entities"
import { DISCOVERED_TRANSLATION_KEYS } from "@/shared/constants"

// Store URLs
const APP_STORE_URL =
  "https://apps.apple.com/us/app/frateca-text-to-speech-audio/id6741859465"
const PLAY_STORE_URL =
  "https://play.google.com/store/apps/details?id=ai.texttospeech.app"

const getQRCodeURL = () => {
  if (typeof window !== "undefined") {
    return `${window.location.origin}/download-app`
  }
  return "https://frateca.com/download-app"
}

/**
 * "server": backend maintenance flag from the status service (same as the
 * mobile app's startup alert) - every client is down, so no app-store block.
 * "new-version": the manual web-only mode toggled in main.tsx.
 */
export type MaintenanceVariant = "server" | "new-version"

export const MaintenanceContent = ({
  variant = "new-version",
}: {
  variant?: MaintenanceVariant
}) => {
  const { t } = useAutoTranslation()
  const qrUrl = getQRCodeURL()
  const isServer = variant === "server"

  return (
    <div className="bg-background fixed inset-0 z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-sm shadow-xl" padding="sm">
        <CardHeader className="pb-2 text-center">
          <div className="bg-primary/10 mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full">
            <Construction className="text-primary h-6 w-6" />
          </div>
          <CardTitle className="text-lg">
            {t(
              isServer
                ? DISCOVERED_TRANSLATION_KEYS.APP_UNDER_MAINTENANCE
                : DISCOVERED_TRANSLATION_KEYS.UNDER_MAINTENANCE
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-center text-sm">
          {isServer ? (
            <p className="text-muted-foreground">
              {t(
                DISCOVERED_TRANSLATION_KEYS.WE_RE_CURRENTLY_PERFORMING_SOME_UPDATES_TO_IMPROVE_YOUR_EXPERIENCE_PLEASE_CHECK_BACK_LATER_THANKS_FOR_YOUR_PATIENCE
              )}
            </p>
          ) : (
            <p className="text-muted-foreground">
              {t(
                DISCOVERED_TRANSLATION_KEYS.WE_ARE_CURRENTLY_WORKING_ON_ANEW_VERSION_OF_OUR_WEB_APP
              )}
              <br />
              {t(
                DISCOVERED_TRANSLATION_KEYS.WE_APOLOGIZE_FOR_THE_INCONVENIENCE_THE_SERVICE_WILL_BE_BACK_ONLINE_SOON
              )}
            </p>
          )}

          <p className="text-muted-foreground">
            {t(
              DISCOVERED_TRANSLATION_KEYS.IF_YOU_HAVE_ANY_QUESTIONS_PLEASE_CONTACT_US_AT
            )}{" "}
            <a
              href="mailto:support@frateca.com"
              className="text-primary font-medium hover:underline"
            >
              support@frateca.com
            </a>
          </p>

          {!isServer && (
            <div className="bg-muted/50 flex items-center gap-3 rounded-lg p-3">
              <div className="shrink-0 rounded-md bg-white p-1.5">
                <QRCodeSVG
                  value={qrUrl}
                  size={72}
                  level="L"
                  includeMargin={false}
                  bgColor="white"
                  fgColor="black"
                />
              </div>
              <div className="flex flex-col items-start gap-2 text-left">
                <p className="text-muted-foreground text-xs">
                  {t(
                    DISCOVERED_TRANSLATION_KEYS.OUR_IOS_AND_ANDROID_APPS_ARE_WORKING_AS_USUAL_SCAN_OR_DOWNLOAD
                  )}
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 gap-1.5 px-2 text-xs"
                    onClick={() => window.open(APP_STORE_URL, "_blank")}
                  >
                    <svg
                      viewBox="0 0 24 24"
                      className="h-3.5 w-3.5"
                      fill="currentColor"
                    >
                      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
                    </svg>
                    App Store
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 gap-1.5 px-2 text-xs"
                    onClick={() => window.open(PLAY_STORE_URL, "_blank")}
                  >
                    <svg
                      viewBox="0 0 24 24"
                      className="h-3.5 w-3.5"
                      fill="currentColor"
                    >
                      <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z" />
                    </svg>
                    Google Play
                  </Button>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

const MaintenanceRouter = () => {
  return (
    <Routes>
      <Route path="/download-app" element={<AppDownloadPage />} />
      <Route path="*" element={<MaintenanceContent />} />
    </Routes>
  )
}

export const Maintenance = () => {
  return (
    <BrowserRouter>
      <ThemeProvider defaultTheme="system" storageKey="theme-light-dark">
        <MaintenanceRouter />
      </ThemeProvider>
    </BrowserRouter>
  )
}
